import React, { useState, useRef } from 'react';
import './ImageUpload.css';
import StorageService from '../services/storage';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  folder: 'products' | 'categories';
  placeholder?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  value, 
  onChange, 
  folder, 
  placeholder = "Click to upload image" 
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string>(value || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setPreview(value || '');
  }, [value]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const imagePath = StorageService.generateImagePath(folder, file.name);
      const downloadURL = await StorageService.uploadImage(file, imagePath);
      onChange(downloadURL);
      setPreview(downloadURL);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    onChange('');
    setPreview('');
  };

  return (
    <div className="image-upload">
      <div className="image-upload-container">
        {preview ? (
          <div className="image-preview">
            <img src={preview} alt="Preview" />
            <div className="image-overlay">
              <button 
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClick}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Change'}
              </button>
              <button 
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleRemoveImage}
                disabled={uploading}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="image-placeholder" onClick={handleClick}>
            <div className="placeholder-content">
              <div className="upload-icon">📷</div>
              <p>{placeholder}</p>
              <small>Click to upload (Max 5MB)</small>
            </div>
          </div>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {uploading && (
        <div className="upload-progress">
          <div className="spinner"></div>
          <span>Uploading image...</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
